import React, { useState } from 'react';
import CustomDatePicker from '../general/CustomDatePicker';
import Swal from 'sweetalert2';

const countryOptions = [
    { value: 'España', label: 'España' },
    { value: 'Chile', label: 'Chile' },
    { value: 'Perú', label: 'Perú' },
    { value: 'Otro', label: 'Otro' },
];

const telefonoPrefixOptions = [
    { value: '+34', label: '+34' },
    { value: '+56', label: '+56' },
    { value: '+51', label: '+51' },
];

const generoOptions = [
    { value: 'HOMBRE', label: 'Hombre' },
    { value: 'MUJER', label: 'Mujer' },
    { value: 'NO_BINARIO', label: 'No binario' },
    { value: 'PREFIERO_NO_DECIRLO', label: 'Prefiero no decirlo' },
];

/**
 * Componente de contenido de QRRegisterPage que puede ser usado tanto en la página real como en el preview
 * @param {Object} props
 * @param {Object} props.tienda - Datos de la tienda
 * @param {Object} props.texts - Textos personalizados
 * @param {Object} props.styles - Estilos personalizados
 * @param {string} props.layout - Layout seleccionado ('classic', 'modern', 'minimal', 'hero')
 * @param {Object} props.layoutOptions - Opciones de layout
 * @param {Object} [props.imagenFondo] - Imagen de fondo de la personalización (con url, cdnUrl, etc.)
 * @param {Function} [props.onSubmit] - Callback cuando se envía el formulario (opcional, para preview)
 * @param {boolean} props.isPreview - Si es true, desactiva funcionalidad interactiva
 */
const QRRegisterPageContent = ({
    tienda,
    texts = {},
    styles = {},
    layout = 'classic',
    layoutOptions = {},
    imagenFondo = null,
    onSubmit,
    isPreview = false
}) => {
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        nombre: '',
        apellido: '',
        email: '',
        telefonoPrefix: '+34',
        telefonoNumber: '',
        fechaCumpleanos: '',
        genero: 'PREFIERO_NO_DECIRLO',
        geographic_info: { country: '', state: '', city: '' },
    });
    const [codigoReferido, setCodigoReferido] = useState('');

    if (!tienda) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-600">
                    <p className="text-xl">Tienda no disponible en preview</p>
                </div>
            </div>
        );
    }

    // Calcular estilos aplicando personalización
    const pageBgColor = styles.pageBackgroundColor || '#EFF6FF';
    const headerBgColor = styles.headerBackgroundColor || '#FFFFFF';
    const headerBorderColor = styles.headerBorderColor || '#2563EB';
    const cardBgColor = styles.cardBackgroundColor || '#FFFFFF';
    const cardBorderRadius = styles.cardBorderRadius || '8px';
    const titleColor = styles.titleColor || '#111827';
    const descriptionColor = styles.descriptionColor || '#4B5563';
    const benefitCardBg = styles.benefitCardBackgroundColor || '#EFF6FF';
    const benefitIconBg = styles.benefitIconBackgroundColor || '#DBEAFE';
    const benefitIconColor = styles.benefitIconColor || '#2563EB';
    const inputBorderColor = styles.inputBorderColor || '#D1D5DB';
    const inputFocusColor = styles.inputFocusColor || '#2563EB';
    const buttonBgColor = styles.buttonBackgroundColor || '#2563EB';
    const buttonTextColor = styles.buttonTextColor || '#FFFFFF';
    const buttonHoverColor = styles.buttonHoverColor || '#1D4ED8';
    const sectionBgColor = styles.sectionBackgroundColor || '#F9FAFB';
    const fontFamily = styles.fontFamily || 'sans-serif';
    const fontSize = styles.fontSize || '16px';

    // Determinar clases CSS según layout y opciones
    const headerClasses = {
        bordered: 'border-b-4',
        gradient: 'bg-gradient-to-r',
        minimal: 'border-b-2'
    }[layoutOptions.headerStyle] || 'border-b-4';

    const logoPositionClasses = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end'
    }[layoutOptions.logoPosition] || 'justify-center';

    const benefitsLayoutClasses = {
        grid: 'grid grid-cols-1 md:grid-cols-3 gap-6',
        list: 'flex flex-col gap-4',
        carousel: 'grid grid-cols-1 md:grid-cols-3 gap-6'
    }[layoutOptions.benefitsLayout] || 'grid grid-cols-1 md:grid-cols-3 gap-6';

    // Función auxiliar para renderizar la sección de beneficios
    const renderBenefitsSection = () => {
        if (layoutOptions.showBenefits === false) return null;

        const benefitsContainerClass = layout === 'modern'
            ? 'rounded-2xl shadow-xl px-6 py-8 sm:p-10'
            : layout === 'minimal'
                ? 'rounded-lg shadow-sm px-4 py-6'
                : 'shadow-sm px-6 py-8 sm:p-8';

        return (
            <div
                className={benefitsContainerClass}
                style={{ backgroundColor: cardBgColor }}
            >
                {layout === 'minimal' ? (
                    <>
                        <h2
                            className="text-xl font-semibold mb-4"
                            style={{ color: titleColor }}
                        >
                            {texts.benefitsTitle}
                        </h2>
                        <div className={`${benefitsLayoutClasses} mb-6`}>
                            <div
                                className="rounded-md p-4 text-center"
                                style={{ backgroundColor: benefitCardBg }}
                            >
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4"
                                    style={{ backgroundColor: benefitIconBg }}
                                >
                                    <svg className="w-5 h-5" style={{ color: benefitIconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-base font-semibold mb-2" style={{ color: titleColor }}>
                                    {texts.benefit1Title}
                                </h3>
                                <p className="text-sm" style={{ color: descriptionColor }}>
                                    {texts.benefit1Description}
                                </p>
                            </div>
                            <div
                                className="rounded-md p-4 text-center"
                                style={{ backgroundColor: benefitCardBg }}
                            >
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4"
                                    style={{ backgroundColor: benefitIconBg }}
                                >
                                    <svg className="w-5 h-5" style={{ color: benefitIconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                                    </svg>
                                </div>
                                <h3 className="text-base font-semibold mb-2" style={{ color: titleColor }}>
                                    {texts.benefit2Title}
                                </h3>
                                <p className="text-sm" style={{ color: descriptionColor }}>
                                    {texts.benefit2Description}
                                </p>
                            </div>
                            <div
                                className="rounded-md p-4 text-center"
                                style={{ backgroundColor: benefitCardBg }}
                            >
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4"
                                    style={{ backgroundColor: benefitIconBg }}
                                >
                                    <svg className="w-5 h-5" style={{ color: benefitIconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                    </svg>
                                </div>
                                <h3 className="text-base font-semibold mb-2" style={{ color: titleColor }}>
                                    {texts.benefit3Title}
                                </h3>
                                <p className="text-sm" style={{ color: descriptionColor }}>
                                    {texts.benefit3Description}
                                </p>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="text-center mb-8">
                            <h2
                                className={`${layout === 'modern' ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl'} font-bold mb-4`}
                                style={{ color: titleColor }}
                            >
                                {texts.benefitsTitle}
                            </h2>
                            <p
                                className={`${layout === 'modern' ? 'text-xl' : 'text-lg'} max-w-2xl mx-auto`}
                                style={{ color: descriptionColor }}
                            >
                                {texts.benefitsDescription}
                            </p>
                        </div>
                        <div className={`${benefitsLayoutClasses} ${layout === 'modern' ? 'mb-10' : 'mb-8'}`}>
                            <div
                                className="rounded-lg p-6 text-center"
                                style={{ backgroundColor: benefitCardBg }}
                            >
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                                    style={{ backgroundColor: benefitIconBg }}
                                >
                                    <svg className="w-6 h-6" style={{ color: benefitIconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold mb-2" style={{ color: titleColor }}>
                                    {texts.benefit1Title}
                                </h3>
                                <p style={{ color: descriptionColor }}>
                                    {texts.benefit1Description}
                                </p>
                            </div>
                            <div
                                className="rounded-lg p-6 text-center"
                                style={{ backgroundColor: benefitCardBg }}
                            >
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                                    style={{ backgroundColor: benefitIconBg }}
                                >
                                    <svg className="w-6 h-6" style={{ color: benefitIconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold mb-2" style={{ color: titleColor }}>
                                    {texts.benefit2Title}
                                </h3>
                                <p style={{ color: descriptionColor }}>
                                    {texts.benefit2Description}
                                </p>
                            </div>
                            <div
                                className="rounded-lg p-6 text-center"
                                style={{ backgroundColor: benefitCardBg }}
                            >
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                                    style={{ backgroundColor: benefitIconBg }}
                                >
                                    <svg className="w-6 h-6" style={{ color: benefitIconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold mb-2" style={{ color: titleColor }}>
                                    {texts.benefit3Title}
                                </h3>
                                <p style={{ color: descriptionColor }}>
                                    {texts.benefit3Description}
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const handleChange = (e) => {
        if (isPreview) {
            Swal.fire('Info', 'El formulario está deshabilitado en el modo de previsualización.', 'info');
            return;
        }
        const { name, value } = e.target;
        if (name.startsWith('geo_')) {
            const key = name.split('_')[1];
            setFormData((prev) => ({
                ...prev,
                geographic_info: { ...prev.geographic_info, [key]: value },
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleCodigoReferidoChange = (e) => {
        if (isPreview) {
            Swal.fire('Info', 'El formulario está deshabilitado en el modo de previsualización.', 'info');
            return;
        }
        setCodigoReferido(e.target.value);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.nombre.trim()) newErrors.nombre = "El nombre es obligatorio.";
        if (!formData.apellido.trim()) newErrors.apellido = "El apellido es obligatorio.";
        if (!formData.email.trim()) newErrors.email = "El correo electrónico es obligatorio.";
        if (!formData.telefonoNumber.trim()) newErrors.telefonoNumber = "El número de teléfono es obligatorio.";
        if (!formData.fechaCumpleanos) newErrors.fechaCumpleanos = "La fecha de nacimiento es obligatoria.";
        if (!formData.genero) newErrors.genero = "El género es obligatorio.";

        const { country, state, city } = formData.geographic_info;
        if ((state || city) && !country) {
            newErrors.geographic_info = "Si ingresas estado o ciudad, debes seleccionar un país.";
        }
        if (city && !state) {
            newErrors.geographic_info = "Si ingresas ciudad, debes ingresar también el estado o región.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (isPreview) {
            Swal.fire('Info', 'El registro está deshabilitado en el modo de previsualización.', 'info');
            return;
        }
        if (!validateForm()) {
            Swal.fire('Error', 'Por favor, corrige los errores en el formulario.', 'error');
            return;
        }
        if (onSubmit) {
            await onSubmit(formData, codigoReferido);
        }
    };

    // Función para renderizar el contenido según el layout
    const renderContent = () => {
        if (layout === 'hero') {
            // Layout Hero: Header grande con imagen de fondo
            return (
                <>
                    {/* Hero Header */}
                    <div
                        className="relative w-full h-96 flex items-center justify-center mb-8"
                        style={{
                            background: imagenFondo
                                ? `linear-gradient(135deg, ${headerBgColor} 0%, ${headerBgColor}dd 100%), url(${imagenFondo.cdnUrl || imagenFondo.url})`
                                : `linear-gradient(135deg, ${headerBgColor} 0%, ${headerBgColor}dd 100%)`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundBlendMode: imagenFondo ? 'overlay' : 'normal'
                        }}
                    >
                        {layoutOptions.showOverlay !== false && (
                            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                        )}
                        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
                            {layoutOptions.showLogo && tienda.logotipo && (
                                <img
                                    src={tienda.logotipo.cdnUrl || tienda.logotipo.url}
                                    alt={`Logo de ${tienda.nombre}`}
                                    className="h-24 w-24 object-contain bg-white rounded-lg shadow-lg p-3 mx-auto mb-6"
                                />
                            )}
                            {layoutOptions.showBrandName !== false && (
                                <h1
                                    className="text-4xl md:text-5xl font-extrabold mb-4"
                                    style={{ color: '#FFFFFF' }}
                                >
                                    {tienda.nombre}
                                </h1>
                            )}
                            {layoutOptions.showSlogan && tienda.descripcion && (
                                <p className="text-xl text-white opacity-90 mb-4">
                                    {tienda.descripcion}
                                </p>
                            )}
                            {layoutOptions.showLocation !== false && (
                                <div className="inline-flex items-center text-sm bg-white bg-opacity-20 rounded-full px-4 py-2 backdrop-blur-sm mt-4">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <address className="not-italic text-white">
                                        {tienda.via} {tienda.calle}, {tienda.numero}, {tienda.poblacion}, {tienda.pais}
                                    </address>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            );
        } else if (layout === 'modern') {
            // Layout Modern: Diseño moderno con gradientes y espaciado amplio
            return (
                <>
                    {/* Header Section - Modern Style */}
                    <div
                        className="bg-gradient-to-br rounded-3xl shadow-xl p-8 sm:p-12 text-center mb-8"
                        style={{
                            background: `linear-gradient(135deg, ${headerBgColor} 0%, ${headerBorderColor} 100%)`,
                            color: '#FFFFFF'
                        }}
                    >
                        <div className={`max-w-3xl mx-auto ${logoPositionClasses === 'justify-center' ? 'text-center' : ''}`}>
                            {layoutOptions.showLogo && tienda.logotipo && (
                                <div className="mb-6">
                                    <img
                                        src={tienda.logotipo.cdnUrl || tienda.logotipo.url}
                                        alt={`Logo de ${tienda.nombre}`}
                                        className="h-24 w-24 object-contain mx-auto bg-white rounded-2xl shadow-lg p-3"
                                    />
                                </div>
                            )}
                            <h1
                                className="text-4xl sm:text-5xl font-extrabold mb-4"
                                style={{ color: '#FFFFFF' }}
                            >
                                {tienda.nombre}
                            </h1>
                            {layoutOptions.showSlogan && tienda.descripcion && (
                                <p
                                    className="text-xl mb-6 opacity-90"
                                    style={{ color: '#FFFFFF' }}
                                >
                                    {tienda.descripcion}
                                </p>
                            )}
                            {layoutOptions.showLocation !== false && (
                                <div className="inline-flex items-center text-sm bg-white bg-opacity-20 rounded-full px-4 py-2 backdrop-blur-sm">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <address className="not-italic">
                                        {tienda.via} {tienda.calle}, {tienda.numero}, {tienda.poblacion}, {tienda.pais}
                                    </address>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            );
        } else if (layout === 'minimal') {
            // Layout Minimal: Diseño limpio y minimalista
            return (
                <>
                    {/* Header Section - Minimal Style */}
                    <div
                        className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6"
                        style={{
                            backgroundColor: headerBgColor,
                            borderBottom: `2px solid ${headerBorderColor}`
                        }}
                    >
                        <div className={`max-w-3xl mx-auto flex items-center ${logoPositionClasses}`}>
                            {layoutOptions.showLogo && tienda.logotipo && (
                                <img
                                    src={tienda.logotipo.cdnUrl || tienda.logotipo.url}
                                    alt={`Logo de ${tienda.nombre}`}
                                    className="h-12 w-12 object-contain mr-4"
                                />
                            )}
                            <div className="flex-1">
                                <h1
                                    className="text-2xl sm:text-3xl font-bold mb-1"
                                    style={{ color: titleColor }}
                                >
                                    {tienda.nombre}
                                </h1>
                                {layoutOptions.showSlogan && tienda.descripcion && (
                                    <p
                                        className="text-sm"
                                        style={{ color: descriptionColor }}
                                    >
                                        {tienda.descripcion}
                                    </p>
                                )}
                            </div>
                        </div>
                        {layoutOptions.showLocation !== false && (
                            <div className="mt-4 text-xs" style={{ color: descriptionColor }}>
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <address className="inline not-italic">
                                    {tienda.via} {tienda.calle}, {tienda.numero}, {tienda.poblacion}, {tienda.pais}
                                </address>
                            </div>
                        )}
                    </div>
                </>
            );
        } else {
            // Layout Classic: Diseño clásico estándar (default)
            return (
                <>
                    {/* Header Section - Classic Style */}
                    <div
                        className={`bg-white rounded-t-2xl shadow-sm p-6 sm:p-8 text-center ${headerClasses}`}
                        style={{
                            backgroundColor: headerBgColor,
                            borderBottomColor: headerBorderColor,
                            borderBottomWidth: layoutOptions.headerStyle === 'minimal' ? '2px' : '4px'
                        }}
                    >
                        <div className={`max-w-3xl mx-auto ${logoPositionClasses === 'justify-center' ? 'text-center' : ''}`}>
                            {layoutOptions.showLogo && tienda.logotipo && (
                                <img
                                    src={tienda.logotipo.cdnUrl || tienda.logotipo.url}
                                    alt={`Logo de ${tienda.nombre}`}
                                    className="h-16 w-16 object-contain mx-auto mb-4"
                                />
                            )}
                            <h1
                                className="text-3xl sm:text-4xl font-extrabold mb-2"
                                style={{ color: titleColor }}
                            >
                                {tienda.nombre}
                            </h1>
                            {layoutOptions.showSlogan && tienda.descripcion && (
                                <p
                                    className="text-lg mb-4"
                                    style={{ color: descriptionColor }}
                                >
                                    {tienda.descripcion}
                                </p>
                            )}
                            {layoutOptions.showLocation !== false && (
                                <div className="inline-flex items-center text-sm" style={{ color: descriptionColor }}>
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <address className="not-italic">
                                        {tienda.via} {tienda.calle}, {tienda.numero}, {tienda.poblacion}, {tienda.pais}, {tienda.region}
                                    </address>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            );
        }
    };

    return (
        <div
            className="min-h-screen py-8 px-4 sm:px-6 lg:px-8"
            style={{
                fontFamily,
                fontSize,
                background: layout === 'hero'
                    ? '#000000'
                    : layout === 'modern'
                        ? `linear-gradient(135deg, ${pageBgColor} 0%, ${headerBorderColor}15 100%)`
                        : layout === 'minimal'
                            ? pageBgColor
                            : `linear-gradient(to bottom right, ${pageBgColor}, #F3F4F6)`
            }}
        >
            {isPreview && (
                <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white text-center py-2 px-4 text-sm font-semibold shadow-lg">
                    📋 Layout: {layout === 'hero' ? 'Hero' : layout === 'modern' ? 'Moderno' : layout === 'minimal' ? 'Minimalista' : 'Clásico'}
                </div>
            )}
            <div className={`mx-auto ${layout === 'minimal' ? 'max-w-3xl' : layout === 'hero' ? 'max-w-5xl' : 'max-w-4xl'} ${isPreview ? 'mt-8' : ''}`}>
                {renderContent()}
                {layout === 'hero' && (
                    <div className="-mt-20 relative z-20">
                        {renderBenefitsSection()}
                    </div>
                )}
                {layout !== 'hero' && renderBenefitsSection()}

                {/* Registration Form */}
                <div
                    className={`${layout === 'hero' ? 'rounded-2xl shadow-2xl -mt-20 relative z-20' : layout === 'modern' ? 'rounded-2xl shadow-xl' : layout === 'minimal' ? 'rounded-lg shadow-sm' : 'rounded-lg'} p-6 sm:p-8 ${layoutOptions.showBenefits !== false && layout !== 'hero' ? '' : 'mt-0'}`}
                    style={{
                        backgroundColor: cardBgColor,
                        borderRadius: layout === 'hero' ? '1rem' : layout === 'modern' ? '1rem' : layout === 'minimal' ? '0.5rem' : cardBorderRadius
                    }}
                >
                    <h3
                        className={`${layout === 'hero' ? 'text-3xl' : layout === 'modern' ? 'text-3xl' : layout === 'minimal' ? 'text-xl' : 'text-2xl'} font-bold text-center mb-8`}
                        style={{ color: titleColor }}
                    >
                        {texts.formTitle}
                    </h3>
                    <form onSubmit={handleRegister} className="space-y-6">
                        {/* Personal Information Section */}
                        <div
                            className="rounded-lg p-6"
                            style={{ backgroundColor: sectionBgColor }}
                        >
                            <h4
                                className="text-lg font-semibold mb-4"
                                style={{ color: titleColor }}
                            >
                                {texts.personalInfoTitle}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre*</label>
                                    <input
                                        type="text"
                                        name="nombre"
                                        value={formData.nombre}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                        style={{
                                            borderColor: inputBorderColor,
                                            focusRingColor: inputFocusColor
                                        }}
                                        placeholder="Tu nombre"
                                        required
                                        disabled={isPreview}
                                    />
                                    {errors.nombre && <p className="mt-1 text-sm text-red-600">{errors.nombre}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Apellido*</label>
                                    <input
                                        type="text"
                                        name="apellido"
                                        value={formData.apellido}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                        style={{
                                            borderColor: inputBorderColor,
                                            focusRingColor: inputFocusColor
                                        }}
                                        placeholder="Tu apellido"
                                        required
                                        disabled={isPreview}
                                    />
                                    {errors.apellido && <p className="mt-1 text-sm text-red-600">{errors.apellido}</p>}
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico*</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                        style={{
                                            borderColor: inputBorderColor,
                                            focusRingColor: inputFocusColor
                                        }}
                                        placeholder="tu@email.com"
                                        required
                                        disabled={isPreview}
                                    />
                                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono*</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        <select
                                            name="telefonoPrefix"
                                            value={formData.telefonoPrefix}
                                            onChange={handleChange}
                                            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                            required
                                            disabled={isPreview}
                                        >
                                            {telefonoPrefixOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="col-span-2">
                                            <input
                                                type="text"
                                                name="telefonoNumber"
                                                value={formData.telefonoNumber}
                                                onChange={handleChange}
                                                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                                style={{
                                                    borderColor: inputBorderColor,
                                                    focusRingColor: inputFocusColor
                                                }}
                                                placeholder="Número de teléfono"
                                                required
                                                disabled={isPreview}
                                            />
                                        </div>
                                    </div>
                                    {errors.telefonoNumber && <p className="mt-1 text-sm text-red-600">{errors.telefonoNumber}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Nacimiento*</label>
                                    <CustomDatePicker
                                        name="fechaCumpleanos"
                                        value={formData.fechaCumpleanos}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                        style={{
                                            borderColor: inputBorderColor,
                                            focusRingColor: inputFocusColor
                                        }}
                                        placeholderText="DD/MM/AAAA"
                                        required
                                        disabled={isPreview}
                                    />
                                    {errors.fechaCumpleanos && <p className="mt-1 text-sm text-red-600">{errors.fechaCumpleanos}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Género*</label>
                                    <select
                                        name="genero"
                                        value={formData.genero}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                        style={{
                                            borderColor: inputBorderColor,
                                            focusRingColor: inputFocusColor
                                        }}
                                        required
                                        disabled={isPreview}
                                    >
                                        {generoOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.genero && <p className="mt-1 text-sm text-red-600">{errors.genero}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Referral Code Section */}
                        <div
                            className="rounded-lg p-6"
                            style={{ backgroundColor: sectionBgColor }}
                        >
                            <h4
                                className="text-lg font-semibold mb-4"
                                style={{ color: titleColor }}
                            >
                                {texts.referralCodeTitle}
                            </h4>
                            <div>
                                <input
                                    type="text"
                                    name="codigoReferido"
                                    value={codigoReferido}
                                    onChange={handleCodigoReferidoChange}
                                    className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                    style={{
                                        borderColor: inputBorderColor,
                                        focusRingColor: inputFocusColor
                                    }}
                                    placeholder={texts.referralCodePlaceholder}
                                    disabled={isPreview}
                                />
                            </div>
                        </div>

                        {/* Geographic Information Section */}
                        <div
                            className="rounded-lg p-6"
                            style={{ backgroundColor: sectionBgColor }}
                        >
                            <h4
                                className="text-lg font-semibold mb-4"
                                style={{ color: titleColor }}
                            >
                                {texts.geographicInfoTitle}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <select
                                        name="geo_country"
                                        value={formData.geographic_info.country}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                        style={{
                                            borderColor: inputBorderColor,
                                            focusRingColor: inputFocusColor
                                        }}
                                        disabled={isPreview}
                                    >
                                        <option value="">Selecciona tu país</option>
                                        {countryOptions.map((c) => (
                                            <option key={c.value} value={c.value}>
                                                {c.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <input
                                        type="text"
                                        name="geo_state"
                                        value={formData.geographic_info.state}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                        style={{
                                            borderColor: inputBorderColor,
                                            focusRingColor: inputFocusColor
                                        }}
                                        placeholder="Estado/Región"
                                        disabled={isPreview || !formData.geographic_info.country}
                                    />
                                </div>

                                <div>
                                    <input
                                        type="text"
                                        name="geo_city"
                                        value={formData.geographic_info.city}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:border-transparent transition-colors"
                                        style={{
                                            borderColor: inputBorderColor,
                                            focusRingColor: inputFocusColor
                                        }}
                                        placeholder="Ciudad"
                                        disabled={isPreview || !formData.geographic_info.country || !formData.geographic_info.state}
                                    />
                                </div>
                            </div>
                            {errors.geographic_info && <p className="mt-1 text-sm text-red-600">{errors.geographic_info}</p>}
                        </div>

                        {/* Submit Button */}
                        <div className="pt-6">
                            <button
                                type="submit"
                                className="w-full px-6 py-3 rounded-lg text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    backgroundColor: buttonBgColor,
                                    color: buttonTextColor,
                                    focusRingColor: buttonBgColor
                                }}
                                onMouseEnter={(e) => !isPreview && (e.target.style.backgroundColor = buttonHoverColor)}
                                onMouseLeave={(e) => !isPreview && (e.target.style.backgroundColor = buttonBgColor)}
                                disabled={isPreview}
                            >
                                {texts.submitButtonText}
                            </button>
                            <p
                                className="mt-4 text-sm text-center"
                                style={{ color: descriptionColor }}
                            >
                                {texts.termsText}
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default QRRegisterPageContent;

